import React, { useState, useEffect, memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogIn, Shield, CheckCircle, Loader2, AlertTriangle,
  Smartphone, Lock, ArrowRight, Zap, BarChart3, Target, Calendar
} from 'lucide-react';
import { haptic } from '../../../shared/utils/haptics';

const SLIDES = [
  {
    icon: <Zap className="w-6 h-6 text-zinc-400" />,
    title: 'My ADHD Tasks',
    subtitle: 'Votre assistant quotidien',
    description: 'Gérez vos tâches et routines avec une application conçue pour les cerveaux neurodivergents.',
  },
  {
    icon: <Calendar className="w-6 h-6 text-zinc-400" />,
    title: 'Planification intelligente',
    subtitle: 'Structure et flexibilité',
    description: 'Un planning visuel avec codes couleurs pour distinguer les tâches vitales, corvées et moments de plaisir.',
  },
  {
    icon: <BarChart3 className="w-6 h-6 text-zinc-400" />,
    title: 'Gamification',
    subtitle: 'Transformez vos tâches en jeu',
    description: "Gagnez de l'expérience, débloquez des badges et maintenez votre motivation.",
  },
  {
    icon: <Target className="w-6 h-6 text-zinc-400" />,
    title: 'Mode Focus',
    subtitle: 'Concentration maximale',
    description: 'Éliminez les distractions avec le mode focus et les sessions Pomodoro intégrées.',
  }
];

export const GoogleOnboarding = memo(function GoogleOnboarding({ onSignIn, onSkipGoogle, isLoading, error }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showLocalOption, setShowLocalOption] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % SLIDES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const handleSignIn = useCallback(() => {
    haptic.tap();
    onSignIn();
  }, [onSignIn]);

  const handleSkipGoogle = useCallback(() => {
    haptic.tap();
    onSkipGoogle();
  }, [onSkipGoogle]);

  const handleShowLocalOption = useCallback(() => {
    haptic.tap();
    setShowLocalOption(true);
  }, []);

  const handleHideLocalOption = useCallback(() => {
    haptic.tap();
    setShowLocalOption(false);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg-base,#09090b)] flex flex-col relative overflow-hidden">
      {/* Background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[10%] w-[300px] h-[300px] bg-zinc-800/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-[20%] right-[-5%] w-[200px] h-[200px] bg-zinc-800/20 rounded-full blur-[80px]" />
      </div>

      {/* Content */}
      <div className="relative flex-grow flex flex-col items-center justify-center px-6 py-12 max-w-md mx-auto w-full">
        {/* Logo area */}
        <div className="mb-10 text-center">
          <motion.div
            className="w-16 h-16 mx-auto glass-card flex items-center justify-center mb-4"
            key={currentSlide}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {SLIDES[currentSlide].icon}
          </motion.div>
        </div>

        {/* Slides */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="text-center mb-10 min-h-[140px]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <h1 className="text-2xl font-semibold text-zinc-100 mb-2 tracking-tight">
              {SLIDES[currentSlide].title}
            </h1>
            <p className="text-sm font-medium text-zinc-400 mb-4">
              {SLIDES[currentSlide].subtitle}
            </p>
            <p className="text-zinc-500 text-sm leading-relaxed max-w-xs mx-auto">
              {SLIDES[currentSlide].description}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Slide indicators */}
        <div className="flex gap-2 mb-10">
          {SLIDES.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => { haptic.tap(); setCurrentSlide(index); }}
              className={`h-1 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-zinc-500 w-6'
                  : 'bg-zinc-800 w-1.5 hover:bg-zinc-700'
              }`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {!showLocalOption ? (
            <motion.div
              key="main"
              className="w-full space-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Google Sign in button */}
              <motion.button
                onClick={handleSignIn}
                disabled={isLoading}
                className="w-full py-3.5 bg-white text-zinc-900 rounded-xl font-medium text-sm flex items-center justify-center gap-3 hover:bg-zinc-100 transition-all disabled:opacity-50"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Connexion en cours...
                  </>
                ) : (
                  <>
                    <img
                      src="https://www.google.com/favicon.ico"
                      alt="Google"
                      className="w-4 h-4"
                    />
                    Continuer avec Google
                  </>
                )}
              </motion.button>

              {/* Skip Google button */}
              <motion.button
                onClick={handleShowLocalOption}
                className="w-full py-3 glass-card text-zinc-400 font-medium text-sm flex items-center justify-center gap-2 hover:text-zinc-300 transition-all"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <Smartphone className="w-4 h-4" />
                Utiliser sans compte Google
              </motion.button>

              {/* Error message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    className="w-full bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-start gap-3 mt-4"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-400 text-xs">{error}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Permissions info */}
              <button
                onClick={handleShowLocalOption}
                className="mt-4 text-zinc-600 text-[10px] flex items-center gap-1.5 mx-auto hover:text-zinc-400 transition-colors"
              >
                <Shield className="w-3 h-3" />
                En savoir plus sur les permissions
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="options"
              className="w-full space-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Google option card */}
              <div className="glass-card p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-zinc-100 font-medium text-sm mb-2">Avec Google</h3>
                    <ul className="text-[10px] text-zinc-500 space-y-1 mb-3">
                      <li className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-zinc-600" />
                        Synchronisation Calendar
                      </li>
                      <li className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-zinc-600" />
                        Sauvegarde Cloud automatique
                      </li>
                      <li className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-zinc-600" />
                        Photos de preuves sur Drive
                      </li>
                    </ul>
                    <motion.button
                      onClick={handleSignIn}
                      disabled={isLoading}
                      className="w-full py-2 bg-zinc-700 text-white rounded-lg font-medium text-xs flex items-center justify-center gap-2 hover:bg-zinc-600 transition-all"
                      whileTap={{ scale: 0.98 }}
                    >
                      {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Se connecter avec Google'}
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Local option card */}
              <div className="glass-card p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lock className="w-4 h-4 text-zinc-500" />
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-zinc-100 font-medium text-sm mb-2">Mode Local</h3>
                    <ul className="text-[10px] text-zinc-500 space-y-1 mb-3">
                      <li className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-zinc-600" />
                        Données sur cet appareil
                      </li>
                      <li className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-zinc-600" />
                        Aucun compte requis
                      </li>
                      <li className="flex items-center gap-1.5 text-zinc-600">
                        <span className="w-3 h-3 text-center text-[8px]">—</span>
                        Pas de synchronisation
                      </li>
                    </ul>
                    <motion.button
                      onClick={handleSkipGoogle}
                      className="w-full py-2 bg-zinc-800 text-zinc-300 rounded-lg font-medium text-xs flex items-center justify-center gap-2 hover:bg-zinc-700 transition-all"
                      whileTap={{ scale: 0.98 }}
                    >
                      Continuer sans Google
                      <ArrowRight className="w-3.5 h-3.5" />
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Back button */}
              <motion.button
                onClick={handleHideLocalOption}
                className="text-zinc-600 text-xs hover:text-zinc-400 transition-colors mx-auto block"
                whileHover={{ x: -4 }}
              >
                ← Retour
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Privacy links */}
        <div className="mt-8 text-center">
          <p className="text-zinc-700 text-[10px] mb-2">
            En continuant, vous acceptez notre
          </p>
          <div className="flex items-center justify-center gap-4 text-[10px]">
            <a href="https://adhdmanager.cosyandsafe.fr/privacy.html" className="text-zinc-500 hover:text-zinc-300 transition-colors">
              Politique de confidentialité
            </a>
            <span className="text-zinc-700">•</span>
            <a href="https://adhdmanager.cosyandsafe.fr/terms.html" className="text-zinc-500 hover:text-zinc-300 transition-colors">
              Conditions d'utilisation
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative p-4 text-center border-t border-white/[0.06]">
        <p className="text-zinc-700 text-[10px]">
          © 2024 My ADHD Tasks Manager
        </p>
      </div>
    </div>
  );
});

// Loading screen component
export const GoogleLoadingScreen = memo(function GoogleLoadingScreen() {
  return (
    <div className="min-h-screen bg-[var(--bg-base,#09090b)] flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[30%] left-[30%] w-[300px] h-[300px] bg-zinc-800/20 rounded-full blur-[100px]" />
      </div>

      <div className="text-center relative z-10">
        <div className="w-14 h-14 mx-auto glass-card flex items-center justify-center mb-6">
          <Zap className="w-6 h-6 text-zinc-500" />
        </div>
        <Loader2 className="w-5 h-5 text-zinc-500 animate-spin mx-auto mb-4" />
        <p className="text-zinc-400 text-sm font-medium">Chargement...</p>
        <p className="text-zinc-600 text-[10px] mt-1">My ADHD Tasks Manager</p>
      </div>
    </div>
  );
});

// User badge component
export const GoogleUserBadge = memo(function GoogleUserBadge({ user, onSignOut, isLocalMode }) {
  const [showMenu, setShowMenu] = useState(false);

  const handleToggleMenu = useCallback(() => {
    haptic.tap();
    setShowMenu(!showMenu);
  }, [showMenu]);

  const handleSignOut = useCallback(() => {
    haptic.tap();
    setShowMenu(false);
    onSignOut();
  }, [onSignOut]);

  if (isLocalMode) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg glass-card">
        <Lock className="w-3.5 h-3.5 text-zinc-600" />
        <span className="text-[10px] font-medium text-zinc-500">Mode Local</span>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="relative">
      <motion.button
        onClick={handleToggleMenu}
        className="flex items-center gap-2 p-1.5 rounded-lg glass-card"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            className="w-6 h-6 rounded-full"
          />
        ) : (
          <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-zinc-300 text-xs font-medium">
            {user.name?.charAt(0) || '?'}
          </div>
        )}
      </motion.button>

      <AnimatePresence>
        {showMenu && (
          <>
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMenu(false)}
            />
            <motion.div
              className="absolute right-0 top-full mt-2 w-60 glass-elevated z-50 overflow-hidden"
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              <div className="p-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-3">
                  {user.picture && (
                    <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
                  )}
                  <div className="truncate">
                    <p className="font-medium text-sm text-zinc-100 truncate">{user.name}</p>
                    <p className="text-[10px] text-zinc-500 truncate">{user.email}</p>
                  </div>
                </div>
              </div>
              <div className="p-2">
                <div className="px-3 py-2 text-[10px] text-zinc-600 flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-zinc-500" />
                  Connecté à Google
                </div>
                <motion.button
                  onClick={handleSignOut}
                  className="w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-red-500/10 rounded-lg flex items-center gap-2 transition-colors"
                  whileHover={{ x: 2 }}
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Se déconnecter
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
});

export default GoogleOnboarding;
